// Default color values from checkout.css mapped to actual hex values
// These match the Tailwind color definitions in tailwind.config.js

import { darken as polishedDarken, mix } from 'polished';
import { getThemeLabelDefaults } from '@repo/checkout/theming/themePropertyRegistry';
import getSafeReadableColor from '../../../utils/getSafeReadableColor';

function darken(color, amount) {
  try {
    return polishedDarken(amount, color);
  } catch (e) {
    return color;
  }
}

// The shade a selected order bump link takes: the same 10% black the store
// mixes in CSS (color-mix in srgb), so the swatch and the page agree.
// polished shortens #990000 to #900; the hex field wants six digits.
function shadeSelected(color) {
  if (!color) return color;
  try {
    const mixed = mix(0.1, '#000', color);
    return mixed.length === 4 ? `#${[...mixed.slice(1)].map(c => c + c).join('')}` : mixed;
  } catch (e) {
    return color;
  }
}

export const BASE_TOKENS_DEFAULTS = {
  colors: 'light',
  radius: 'sm',
  colorBackground: { light: '', dark: '' },
  colorSurface: { light: '', dark: '' },
  colorSurfaceAccent: { light: '', dark: '' },
  fontMain: {
    family: 'Inter',
  },
  fontHeading: {
    family: '',
    size: 'md',
    weight: 700,
    align: 'left',
  },
  fontBody: {
    family: '',
    size: 'md',
    weight: 400,
    align: 'left',
  },
};

export const BASE_VARIABLES_DEFAULTS = {
  tokens: BASE_TOKENS_DEFAULTS,
  layout: {
    background: 'color',
    shadowColor: {
      light: '#f6f6f6',
      dark: '#111111',
    },
    shadowWidth: '',
    backgroundWidth: 'half',
    // Layout-specific settings
    centerLayout: {
      backgroundColor: { light: '', dark: '' },
      contentBackgroundColor: { light: '', dark: '' },
      formBackgroundColor: { light: '', dark: '' },
      backgroundImage: null,
      backgroundPosition: 'center',
    },
    doubleLayout: {
      backgroundColor: { light: '', dark: '' },
      contentBackgroundColor: { light: '', dark: '' },
      formBackgroundColor: { light: '', dark: '' },
      formPosition: 'right',
      backgroundWidth: 'half',
    },
    splitLayout: {
      backgroundColor: { light: '', dark: '' },
      contentBackgroundColor: { light: '', dark: '' },
      formBackgroundColor: { light: '', dark: '' },
      backgroundImage: null,
      backgroundPosition: 'center',
      mainColumn: 'left',
    },
  },
  elements: {
    container: {
      enabled: true,
      styles: {},
    },
    product: {
      enabled: true,
      styles: {
        textAlign: 'left',
      },
      logo: {
        enabled: true,
        hostedPageLogoStyle: 'logoBanner',
        logoBanner: {
          enabled: true,
          bannerStyle: 'full',
          styles: {},
        },
        logoBubble: {
          enabled: true,
          styles: {
            size: 80,
            textAlign: 'center',
          },
        },
      },
      title: {
        enabled: true,
        styles: {},
      },
      pricing: {
        enabled: true,
        price: {
          enabled: true,
          styles: {},
        },
        interval: {
          enabled: true,
          styles: {},
        },
        discountedFromPrice: {
          enabled: true,
          styles: {},
        },
        trialPeriod: {
          enabled: true,
          styles: {},
        },
        trialThenPrice: {
          enabled: true,
          styles: {},
        },
        startDate: {
          enabled: true,
          styles: {},
        },
        planIterations: {
          enabled: true,
          styles: {},
        },
        prorationRenews: {
          enabled: true,
          styles: {},
        },
        prorationAmount: {
          enabled: true,
          styles: {},
        },
        setupFee: {
          enabled: true,
          styles: {},
        },
        badge: {
          enabled: true,
          styles: {},
        },
      },
      pricePicker: {
        styles: {},
        tabs: { styles: {}, selectedStyles: {}, name: { styles: {} } },
        title: { enabled: true, styles: {} },
        description: { enabled: true, styles: {} },
        option: {
          styles: {},
          selectedStyles: {},
          hoverStyles: {},
          indicator: {
            enabled: true,
            // Seed the defaults so the dashboard selects show a value (they
            // display the form value; size/select fields have no swatch-style
            // default). Shape drives the selected-mark look (see checkout.css).
            styles: { size: 16, borderWidth: 2, borderRadius: 9999, shape: 'dot' },
            selectedStyles: {},
          },
          badge: { enabled: true, styles: {} },
          label: { enabled: true, styles: {} },
          description: { enabled: true, styles: {} },
          interval: { enabled: true, styles: {} },
        },
      },
      event: {
        enabled: true,
        styles: {},
        location: {
          enabled: true,
          styles: {},
        },
        date: {
          enabled: true,
          styles: {},
        },
        virtual: {
          enabled: true,
          styles: {},
        },
        meetingLink: {
          enabled: true,
        },
      },
    },
    description: {
      enabled: true,
      styles: {},
      headings: {
        enabled: true,
        styles: {},
      },
      body: {
        enabled: true,
        styles: {},
      },
      links: {
        enabled: true,
        styles: {},
      },
      quotes: {
        enabled: true,
        styles: {},
      },
      bullets: {
        type: 'dot',
        align: 'middle',
        styles: {},
      },
      collapsibles: {
        title: {
          enabled: true,
          styles: {},
        },
        body: {
          enabled: true,
          styles: {},
        },
        icon: {
          styles: {},
          type: 'chevron',
        },
        border: {
          enabled: true,
          styles: {},
        },
      },
    },
    checkout: {
      enabled: true,
      styles: {},
      divider: {
        enabled: true,
        styles: {},
      },
      statusBadges: {
        styles: {},
      },
      cards: {
        styles: {},
        selectedStyles: {},
        detail: { styles: {} },
        badge: { styles: {} },
        name: { enabled: true, styles: {} },
        description: { enabled: true, styles: {} },
        price: { enabled: true, styles: {} },
        discountedFromPrice: { enabled: true, styles: {} },
        groupName: { enabled: true, styles: {} },
      },
      variants: {
        enabled: true,
        styles: {},
        variantOptions: {
          enabled: true,
          styles: {},
          selectedStyles: {},
        },
      },
      ticketGroups: {
        enabled: true,
        styles: {},
        ticketTypes: {
          enabled: true,
          styles: {},
          selectedStyles: {},
          fee: {
            enabled: true,
            styles: {},
          },
          remaining: {
            enabled: true,
            styles: {},
          },
          saleDate: {
            enabled: true,
            styles: {},
          },
        },
      },
      form: {
        enabled: true,
        styles: {},
        groups: {
          styles: {},
          phones: 'stack',
        },
        heading: { styles: {} },
        subheading: { styles: {} },
        divider: { styles: {} },
        fields: {
          enabled: true,
          styles: {},
          label: {
            enabled: true,
            styles: {},
          },
          input: {
            enabled: true,
            styles: {
              boxShadow: 'sm',
            },
            placeholder: {
              enabled: true,
            },
            placeholderStyles: {},
          },
        },
        pwyw: {
          enabled: true,
          styles: {},
          minPrice: {
            enabled: true,
            styles: {},
          },
        },
        manualPayments: {
          enabled: true,
          styles: {},
          selectedStyles: {},
          name: {
            enabled: true,
            styles: {},
          },
          description: {
            enabled: true,
            styles: {},
          },
          instructions: {
            enabled: true,
            styles: {},
          },
        },
        priceBreakdown: {
          enabled: true,
          styles: {
            alignment: 'right',
          },
          subtotal: {
            enabled: true,
            styles: {},
          },
          setupFee: {
            enabled: true,
          },
          couponDiscount: {
            enabled: true,
            styles: {},
          },
          bulkDiscount: {
            enabled: true,
          },
          fees: {
            enabled: true,
            styles: {},
          },
          processingFeeContribution: {
            styles: {},
          },
          tax: {
            enabled: true,
            styles: {},
          },
          total: {
            enabled: true,
            styles: {},
          },
        },
        coverProcessingFees: {
          styles: {},
          selectedStyles: {},
          checkbox: { styles: {}, selectedStyles: {} },
          switch: { styles: {}, selectedStyles: {} },
          indicator: { styles: {}, selectedStyles: {} },
          label: { styles: {} },
          amount: { styles: {} },
        },
        button: {
          enabled: true,
          styles: {},
          priceOnButton: {
            enabled: true,
            styles: {},
          },
        },
        confirmation: {
          mark: {
            styles: {},
          },
          heading: {
            styles: {},
          },
          text: {
            styles: {},
          },
        },
        orPayWithCard: {
          enabled: true,
          styles: {},
        },
        securePayments: {
          enabled: true,
          styles: {},
        },
      },
    },
    poweredByStripe: {
      enabled: true,
      styles: {
        badgeStyle: 'auto',
      },
    },
    // NOTE: `poweredByCheckoutPage` is deliberately omitted here. Leaving its
    // `enabled` unset is what lets resolveBadge fall back to the seller-level
    // `showCheckoutPageBranding` global. Do not add a default — it would break
    // the global inheritance and the grandfathering of existing paid sellers.
  },
  labels: getThemeLabelDefaults(),
  customCss: '',
  locale: 'en-US',
};

export const getDefaultThemeColors = (
  colorPrimary,
  brandColor,
  textPrimary,
  textSecondary,
  colorSecondary,
  textTertiary,
  priceBreakdownColor,
) => ({
  // Primary colors
  colorPrimary: {
    light: colorPrimary?.light || brandColor || '#231c1e', // black
    dark: colorPrimary?.dark || brandColor || '#231c1e', // same as light
  },
  colorPrimaryDark: {
    light: darken(colorPrimary?.light || brandColor || '#231c1e', 0.1),
    dark: darken(colorPrimary?.dark || brandColor || '#231c1e', 0.1),
  },
  colorSecondary: {
    light: colorSecondary?.light || colorPrimary?.light || brandColor || '#231c1e',
    dark: colorSecondary?.dark || colorPrimary?.dark || brandColor || '#231c1e',
  },
  textPrimary: {
    light: textPrimary?.light || '#231c1e', // gray-900
    dark: textPrimary?.dark || '#f5f5f5', // neutral-100
  },
  textSecondary: {
    light: textSecondary?.light || '#7f6d72', // gray-500
    dark: textSecondary?.dark || '#d4d4d4', // neutral-300
  },
  textTertiary: {
    light: textTertiary?.light || '#b0a3a8', // gray-400
    dark: textTertiary?.dark || '#a3a3a3', // neutral-400
  },
  priceBreakdownColor: {
    light: priceBreakdownColor?.light || textSecondary?.light || '#7f6d72',
    dark: priceBreakdownColor?.dark || textSecondary?.dark || '#d4d4d4',
  },
  colorBackground: {
    light: '#f9f9f7', // neutralgray
    dark: '#141414',
  },
  colorSurface: {
    light: '#ffffff', // white
    dark: '#1c1c1c',
  },
  colorSurfaceAccent: {
    light: '#ffffff', // white
    dark: '#1c1c1c',
  },
});

// Layout defaults
const PER_LAYOUT_COLOR_DEFAULTS = {
  backgroundColor: {
    light: '#f9f9f7', // neutralgray
    dark: '#141414',
  },
  contentBackgroundColor: {
    light: '#ffffff', // white/surface
    dark: '#1c1c1c',
  },
  formBackgroundColor: {
    light: '#ffffff', // white/surface
    dark: '#1c1c1c',
  },
};

export const DEFAULT_LAYOUT_COLORS = {
  shadowColor: {
    light: '#f6f6f6',
    dark: '#111111',
  },
  centerLayout: {
    ...PER_LAYOUT_COLOR_DEFAULTS,
  },
  doubleLayout: {
    ...PER_LAYOUT_COLOR_DEFAULTS,
    contentBackgroundColor: {
      light: '#f9f9f7', // surfaceAccent — branded panel for double
      dark: '#262626',
    },
  },
  splitLayout: {
    ...PER_LAYOUT_COLOR_DEFAULTS,
  },
};

// Element-specific defaults
// The call out box's background is a 4% wash of its text over the card, the
// swatch twin of the color-mix in the checkout registry.
const washOver = (text, surface) => {
  const wash = mode =>
    text?.[mode] && surface?.[mode] ? mix(0.04, text[mode], surface[mode]) : undefined;
  return { light: wash('light'), dark: wash('dark') };
};

const getDefaultElementColors = themeColors => {
  // Build cards defaults first so sections can reference them
  const cards = {
    styles: {
      backgroundColor: {
        light: '#ffffff', // white
        dark: '#262626', // neutral-800
      },
      borderColor: {
        light: '#e0d9dd', // gray-200
        dark: '#525252', // neutral-600
      },
    },
    selectedStyles: {
      backgroundColor: {
        light: '#ffffff', // white
        dark: '#404040', // neutral-700
      },
      borderColor: {
        light: themeColors.colorSecondary.light,
        dark: themeColors.colorSecondary.dark,
      },
    },
    detail: {
      styles: {
        color: {
          light: themeColors.textSecondary.light,
          dark: themeColors.textSecondary.dark,
        },
      },
    },
    name: {
      styles: {
        color: {
          light: themeColors.textPrimary.light,
          dark: themeColors.textPrimary.dark,
        },
      },
    },
    description: {
      styles: {
        color: {
          light: themeColors.textSecondary.light,
          dark: themeColors.textSecondary.dark,
        },
      },
    },
    price: {
      styles: {
        color: {
          light: themeColors.textPrimary.light,
          dark: themeColors.textPrimary.dark,
        },
      },
    },
    groupName: {
      styles: {
        color: {
          light: themeColors.textPrimary.light,
          dark: themeColors.textPrimary.dark,
        },
      },
    },
    badge: {
      styles: {
        // Text derives a contrast-safe colour against the pill background (the
        // secondary accent), mirroring the CheckoutCSS runtime derivation.
        color: {
          light: getSafeReadableColor(themeColors.colorSecondary.light),
          dark: getSafeReadableColor(themeColors.colorSecondary.dark),
        },
        backgroundColor: {
          light: themeColors.colorSecondary.light,
          dark: themeColors.colorSecondary.dark,
        },
        // Border defaults to the background so the swatch shows a hex and the
        // border is invisible until a contrasting colour is set.
        borderColor: {
          light: themeColors.colorSecondary.light,
          dark: themeColors.colorSecondary.dark,
        },
      },
    },
  };

  return {
    checkout: {
      divider: {
        enabled: true,
        styles: {
          borderColor: {
            light: '#f0eeee', // gray-100
            dark: '#404040', // neutral-700
          },
        },
      },
      styles: {
        backgroundColor: {
          light: '#ffffff', // white
          dark: '#1c1c1c',
        },
        color: {
          light: themeColors.textPrimary.light,
          dark: themeColors.textPrimary.dark,
        },
        borderColor: {
          light: 'transparent',
          dark: 'transparent',
        },
        boxShadowColor: {
          light: '#e0d9dd', // gray-200
          dark: '#111111',
        },
      },
      form: {
        styles: {
          color: {
            light: themeColors.textPrimary.light,
            dark: themeColors.textPrimary.dark,
          },
        },
        heading: {
          styles: {
            color: {
              light: themeColors.textPrimary.light,
              dark: themeColors.textPrimary.dark,
            },
          },
        },
        subheading: {
          styles: {
            color: {
              light: themeColors.textPrimary.light,
              dark: themeColors.textPrimary.dark,
            },
          },
        },
        fields: {
          styles: {
            backgroundColor: {
              light: 'transparent',
              dark: 'transparent',
            },
            color: {
              light: themeColors.textPrimary.light,
              dark: themeColors.textPrimary.dark,
            },
          },
          field: {
            styles: {
              backgroundColor: {
                light: 'transparent',
                dark: 'transparent',
              },
              color: {
                light: themeColors.textPrimary.light,
                dark: themeColors.textPrimary.dark,
              },
            },
          },
          input: {
            styles: {
              color: {
                light: themeColors.textPrimary.light,
                dark: themeColors.textPrimary.dark,
              },
              backgroundColor: {
                light: '#ffffff', // white
                dark: '#262626', // neutral-800
              },
              borderColor: {
                light: '#f0eeee', // gray-100
                dark: '#525252', // neutral-600
              },
              boxShadowColor: {
                light: '#e0d9dd', // gray-200
                dark: '#111111',
              },
              boxShadow: 'sm',
            },
            focusStyles: {
              // color, backgroundColor and borderColor inherit from styles via CSS fallbacks
            },
            placeholder: {
              enabled: true,
              styles: {
                color: {
                  light: '#c0b6ba', // gray-300
                  dark: '#737373', // neutral-500
                },
              },
            },
            placeholderStyles: {
              color: {
                light: '#c0b6ba', // gray-300
                dark: '#737373', // neutral-500
              },
            },
          },
          label: {
            styles: {
              color: {
                light: themeColors.textPrimary.light,
                dark: themeColors.textPrimary.dark,
              },
            },
            focusStyles: {
              // color inherits from styles via CSS fallbacks
            },
          },
          description: {
            styles: {
              color: {
                light: themeColors.textSecondary.light,
                dark: themeColors.textSecondary.dark,
              },
            },
          },
        },
        button: {
          styles: {
            backgroundColor: {
              light: themeColors.colorPrimary.light,
              dark: themeColors.colorPrimary.dark,
            },
            color: {
              light: getSafeReadableColor(themeColors.colorPrimary.light),
              dark: getSafeReadableColor(themeColors.colorPrimary.dark),
            },
            borderColor: {
              light: themeColors.colorPrimary.light,
              dark: themeColors.colorPrimary.dark,
            },
          },
          hoverStyles: {
            backgroundColor: {
              light: themeColors.colorPrimaryDark.light,
              dark: themeColors.colorPrimaryDark.dark,
            },
            color: {
              light: getSafeReadableColor(themeColors.colorPrimaryDark.light),
              dark: getSafeReadableColor(themeColors.colorPrimaryDark.dark),
            },
            borderColor: {
              light: themeColors.colorPrimaryDark.light,
              dark: themeColors.colorPrimaryDark.dark,
            },
          },
        },
        orderBumps: {
          styles: {
            backgroundColor: cards.styles.backgroundColor,
            borderColor: cards.styles.borderColor,
          },
          selectedStyles: {
            backgroundColor: cards.selectedStyles.backgroundColor,
            borderColor: cards.selectedStyles.borderColor,
          },
          title: {
            styles: {
              color: cards.name.styles.color,
            },
          },
          heading: {
            styles: {
              color: cards.groupName.styles.color,
            },
          },
          features: {
            styles: {
              color: cards.name.styles.color,
            },
          },
          callout: {
            styles: {
              color: cards.name.styles.color,
              backgroundColor: washOver(cards.name.styles.color, cards.styles.backgroundColor),
              borderColor: cards.styles.borderColor,
            },
            icon: {
              styles: {
                color: cards.name.styles.color,
              },
            },
          },
          callToAction: {
            styles: {
              color: cards.name.styles.color,
            },
          },
          link: {
            styles: {
              color: cards.name.styles.color,
            },
            selectedStyles: {
              color: {
                light: shadeSelected(cards.name.styles.color.light),
                dark: shadeSelected(cards.name.styles.color.dark),
              },
            },
          },
          shortDescription: {
            styles: {
              color: cards.description.styles.color,
            },
          },
          description: {
            styles: {
              color: cards.description.styles.color,
            },
          },
          price: {
            styles: {
              color: cards.price.styles.color,
            },
          },
          discountedFromPrice: {
            styles: {
              color: cards.detail.styles.color,
            },
          },
          setupFee: {
            styles: {
              color: cards.detail.styles.color,
            },
          },
          badge: {
            styles: {
              backgroundColor: {
                light: themeColors.colorSecondary.light,
                dark: themeColors.colorSecondary.dark,
              },
              color: {
                light: '#ffffff',
                dark: '#ffffff',
              },
            },
          },
          indicator: {
            styles: {
              color: {
                light: themeColors.colorSecondary.light,
                dark: themeColors.colorSecondary.dark,
              },
              borderColor: cards.styles.borderColor,
            },
            selectedStyles: {
              borderColor: cards.selectedStyles.borderColor,
            },
          },
        },
        manualPayments: {
          styles: {
            backgroundColor: cards.styles.backgroundColor,
            color: {
              light: themeColors.textPrimary.light,
              dark: themeColors.textPrimary.dark,
            },
            borderColor: cards.styles.borderColor,
          },
          selectedStyles: {
            backgroundColor: cards.selectedStyles.backgroundColor,
            color: {
              light: themeColors.textPrimary.light,
              dark: themeColors.textPrimary.dark,
            },
            borderColor: cards.selectedStyles.borderColor,
          },
          name: {
            styles: {
              color: cards.name.styles.color,
            },
          },
          description: {
            styles: {
              color: cards.description.styles.color,
            },
          },
          instructions: {
            styles: {
              color: cards.detail.styles.color,
            },
          },
        },
        coverProcessingFees: {
          styles: {
            backgroundColor: cards.styles.backgroundColor,
            borderColor: cards.styles.borderColor,
          },
          selectedStyles: {
            backgroundColor: cards.selectedStyles.backgroundColor,
            borderColor: cards.selectedStyles.borderColor,
          },
          label: {
            styles: {
              color: {
                light: themeColors.textPrimary.light,
                dark: themeColors.textPrimary.dark,
              },
            },
          },
          amount: {
            styles: {
              color: {
                light: themeColors.textPrimary.light,
                dark: themeColors.textPrimary.dark,
              },
            },
          },
        },
        discountCode: {
          link: {
            styles: {
              color: {
                light: themeColors.textPrimary.light,
                dark: themeColors.textPrimary.dark,
              },
            },
          },
          applyButton: {
            styles: {
              color: {
                light: '#231c1e', // gray-900
                dark: '#f5f5f5', // neutral-100
              },
            },
          },
          code: {
            styles: {
              color: {
                light: '#231c1e', // gray-900
                dark: '#f5f5f5', // neutral-100
              },
              backgroundColor: {
                light: '#e0d9dd', // gray-200
                dark: '#525252', // neutral-600
              },
            },
          },
          discount: {
            styles: {
              color: {
                light: '#231c1e', // gray-900
                dark: '#f5f5f5', // neutral-100
              },
            },
          },
        },
        priceBreakdown: {
          styles: {
            color: {
              light: themeColors.textSecondary.light, // text-secondary — matches CSS var(--cp_text-secondary)
              dark: themeColors.textSecondary.dark,
            },
          },
          subtotal: {
            styles: {
              color: {
                light: themeColors.priceBreakdownColor.light,
                dark: themeColors.priceBreakdownColor.dark,
              },
            },
          },
          setupFee: {
            styles: {
              color: {
                light: themeColors.priceBreakdownColor.light,
                dark: themeColors.priceBreakdownColor.dark,
              },
            },
          },
          discount: {
            styles: {
              color: {
                light: themeColors.priceBreakdownColor.light,
                dark: themeColors.priceBreakdownColor.dark,
              },
            },
          },
          couponDiscount: {
            styles: {
              color: {
                light: themeColors.priceBreakdownColor.light,
                dark: themeColors.priceBreakdownColor.dark,
              },
            },
          },
          bulkDiscount: {
            styles: {
              color: {
                light: themeColors.priceBreakdownColor.light,
                dark: themeColors.priceBreakdownColor.dark,
              },
            },
          },
          fee: {
            styles: {
              color: {
                light: themeColors.priceBreakdownColor.light,
                dark: themeColors.priceBreakdownColor.dark,
              },
            },
          },
          fees: {
            styles: {
              color: {
                light: themeColors.priceBreakdownColor.light,
                dark: themeColors.priceBreakdownColor.dark,
              },
            },
          },
          processingFeeContribution: {
            styles: {
              color: {
                light: themeColors.priceBreakdownColor.light,
                dark: themeColors.priceBreakdownColor.dark,
              },
            },
          },
          stripeTax: {
            styles: {
              color: {
                light: themeColors.priceBreakdownColor.light,
                dark: themeColors.priceBreakdownColor.dark,
              },
            },
          },
          total: {
            styles: {
              color: {
                light: themeColors.priceBreakdownColor.light,
                dark: themeColors.priceBreakdownColor.dark,
              },
            },
          },
          startDate: {
            styles: {
              color: {
                light: themeColors.priceBreakdownColor.light,
                dark: themeColors.priceBreakdownColor.dark,
              },
            },
          },
          trial: {
            styles: {
              color: {
                light: themeColors.priceBreakdownColor.light,
                dark: themeColors.priceBreakdownColor.dark,
              },
            },
          },
          dueNow: {
            styles: {
              color: {
                light: themeColors.priceBreakdownColor.light,
                dark: themeColors.priceBreakdownColor.dark,
              },
            },
          },
          proration: {
            styles: {
              color: {
                light: themeColors.priceBreakdownColor.light,
                dark: themeColors.priceBreakdownColor.dark,
              },
            },
          },
        },
        orPayWithCard: {
          styles: {
            color: {
              light: themeColors.textTertiary.light,
              dark: themeColors.textTertiary.dark,
            },
            borderColor: {
              light: '#f0eeee', // gray-100
              dark: '#404040', // neutral-700
            },
          },
        },
        securePayments: {
          styles: {
            color: {
              light: themeColors.textTertiary.light,
              dark: themeColors.textTertiary.dark,
            },
          },
        },
      },
      confirmation: {
        mark: {
          styles: {
            color: {
              light: themeColors.colorPrimary.light,
              dark: themeColors.colorPrimary.dark,
            },
          },
        },
        heading: {
          styles: {
            color: {
              light: themeColors.textPrimary.light,
              dark: themeColors.textPrimary.dark,
            },
          },
        },
        text: {
          styles: {
            color: {
              light: themeColors.textPrimary.light,
              dark: themeColors.textPrimary.dark,
            },
          },
        },
      },
      cards,
      variants: {
        name: {
          styles: {
            color: cards.groupName.styles.color,
          },
        },
        variantOptions: {
          styles: {
            backgroundColor: cards.styles.backgroundColor,
            borderColor: cards.styles.borderColor,
          },
          selectedStyles: {
            backgroundColor: cards.selectedStyles.backgroundColor,
            borderColor: cards.selectedStyles.borderColor,
          },
          name: {
            styles: {
              color: cards.name.styles.color,
            },
          },
          description: {
            styles: {
              color: cards.description.styles.color,
            },
          },
          price: {
            styles: {
              color: cards.price.styles.color,
            },
          },
          discountedFromPrice: {
            styles: {
              color: cards.detail.styles.color,
            },
          },
          planIterations: {
            styles: {
              color: cards.detail.styles.color,
            },
          },
          plusSign: {
            enabled: true,
            styles: {
              color: {
                light: themeColors.textPrimary.light,
                dark: themeColors.textPrimary.dark,
              },
            },
          },
        },
      },
      ticketGroups: {
        name: {
          styles: {
            color: cards.groupName.styles.color,
          },
        },
        description: {
          styles: {
            color: cards.detail.styles.color,
          },
        },
        ticketTypes: {
          styles: {
            backgroundColor: cards.styles.backgroundColor,
            borderColor: cards.styles.borderColor,
          },
          selectedStyles: {
            backgroundColor: cards.selectedStyles.backgroundColor,
            borderColor: cards.selectedStyles.borderColor,
          },
          name: {
            styles: {
              color: cards.name.styles.color,
            },
          },
          description: {
            styles: {
              color: cards.description.styles.color,
            },
          },
          price: {
            styles: {
              color: cards.price.styles.color,
            },
          },
          discountedFromPrice: {
            styles: {
              color: cards.detail.styles.color,
            },
          },
          fee: {
            styles: {
              color: cards.detail.styles.color,
            },
          },
          remaining: {
            styles: {
              color: cards.detail.styles.color,
            },
          },
          saleDate: {
            styles: {
              color: cards.detail.styles.color,
            },
          },
          total: {
            styles: {
              color: cards.detail.styles.color,
            },
          },
        },
      },
      statusBadges: {
        soldOutColor: {
          light: '#ffffff', // white
          dark: '#ffffff', // same as light
        },
        soldOutBackgroundColor: {
          light: '#231c1e', // gray-900
          dark: '#231c1e', // same as light
        },
        canceledColor: {
          light: '#ffffff', // white
          dark: '#ffffff', // same as light
        },
        canceledBackgroundColor: {
          light: '#c81e1e', // red-500
          dark: '#c81e1e', // same as light
        },
        unavailableColor: {
          light: '#ffffff', // white
          dark: '#ffffff', // same as light
        },
        unavailableBackgroundColor: {
          light: '#7f6d72', // gray-500
          dark: '#7f6d72', // same as light
        },
        onSaleSoonColor: {
          light: '#ffffff', // white
          dark: '#ffffff', // same as light
        },
        onSaleSoonBackgroundColor: {
          light: '#88c571', // green-300
          dark: '#88c571', // same as light
        },
        notOnSaleColor: {
          light: '#ffffff', // white
          dark: '#ffffff', // same as light
        },
        notOnSaleBackgroundColor: {
          light: '#7f6d72', // gray-500
          dark: '#7f6d72', // same as light
        },
      },
    },
    product: {
      styles: {
        color: {
          light: themeColors.textPrimary.light,
          dark: themeColors.textPrimary.dark,
        },
      },
      title: {
        styles: {
          color: {
            light: themeColors.textPrimary.light,
            dark: themeColors.textPrimary.dark,
          },
        },
      },
      event: {
        date: {
          styles: {
            color: {
              light: themeColors.textPrimary.light,
              dark: themeColors.textPrimary.dark,
            },
          },
        },
        location: {
          styles: {
            color: {
              light: themeColors.textPrimary.light,
              dark: themeColors.textPrimary.dark,
            },
          },
        },
        virtual: {
          styles: {
            color: {
              light: themeColors.textPrimary.light,
              dark: themeColors.textPrimary.dark,
            },
          },
        },
      },
      pricing: {
        price: {
          styles: {
            color: {
              light: themeColors.textPrimary.light,
              dark: themeColors.textPrimary.dark,
            },
          },
        },
        interval: {
          styles: {
            color: {
              light: themeColors.textPrimary.light,
              dark: themeColors.textPrimary.dark,
            },
          },
        },
        // The pay-what-you-want label copies the price — same default colour.
        label: {
          styles: {
            color: {
              light: themeColors.textPrimary.light,
              dark: themeColors.textPrimary.dark,
            },
          },
        },
        discountedFromPrice: {
          styles: {
            color: {
              light: '#231c1e', // gray-900
              dark: '#231c1e', // same as light
            },
          },
        },
        setupFee: {
          styles: {
            color: {
              light: themeColors.textSecondary.light,
              dark: themeColors.textSecondary.dark,
            },
          },
        },
        trialPeriod: {
          styles: {
            color: {
              light: themeColors.textPrimary.light, // text-primary — matches CSS var(--cp_text-primary)
              dark: themeColors.textPrimary.dark,
            },
          },
        },
        startDate: {
          styles: {
            color: {
              light: themeColors.textSecondary.light,
              dark: themeColors.textSecondary.dark,
            },
          },
        },
        planIterations: {
          styles: {
            color: {
              light: themeColors.textSecondary.light,
              dark: themeColors.textSecondary.dark,
            },
          },
        },
        prorationRenews: {
          styles: {
            color: {
              light: themeColors.textSecondary.light,
              dark: themeColors.textSecondary.dark,
            },
          },
        },
        prorationAmount: {
          styles: {
            color: {
              light: themeColors.textSecondary.light,
              dark: themeColors.textSecondary.dark,
            },
          },
        },
        // Single-price highlight pill — text derives a contrast-safe colour
        // against the badge background (the secondary accent), mirroring the
        // CheckoutCSS runtime derivation.
        badge: {
          styles: {
            color: {
              light: getSafeReadableColor(themeColors.colorSecondary.light),
              dark: getSafeReadableColor(themeColors.colorSecondary.dark),
            },
            backgroundColor: {
              light: themeColors.colorSecondary.light,
              dark: themeColors.colorSecondary.dark,
            },
            borderColor: {
              light: themeColors.colorSecondary.light,
              dark: themeColors.colorSecondary.dark,
            },
          },
        },
      },
      pricePicker: {
        // The heading follows the Cards group name (a saved one is resolved in
        // resolveCardsInheritance); the description is plain secondary text.
        title: {
          styles: {
            color: cards.groupName.styles.color,
          },
        },
        description: {
          styles: {
            color: {
              light: themeColors.textSecondary.light,
              dark: themeColors.textSecondary.dark,
            },
          },
        },
        tabs: {
          name: {
            styles: {
              color: cards.groupName.styles.color,
            },
          },
          styles: {
            // The segmented-control track: what --cp_token-surface-accent
            // resolves to (neutralgray / dark surface) — the app default grey.
            backgroundColor: {
              light: '#f0eeee', // gray-100 — the app's track/subtle grey
              dark: '#262626', // one step above the dark content surface
            },
          },
          selectedStyles: {
            backgroundColor: cards.selectedStyles.backgroundColor,
          },
        },
        option: {
          styles: {
            backgroundColor: cards.styles.backgroundColor,
            borderColor: cards.styles.borderColor,
            color: cards.name.styles.color,
          },
          selectedStyles: {
            backgroundColor: cards.selectedStyles.backgroundColor,
            borderColor: {
              light: themeColors.colorSecondary.light,
              dark: themeColors.colorSecondary.dark,
            },
          },
          hoverStyles: {
            borderColor: {
              light: '#e0d9dd', // gray-200 — matches --cp_border-default
              dark: '#525252', // neutral-600
            },
          },
          label: {
            styles: {
              color: cards.name.styles.color,
            },
          },
          description: {
            styles: {
              color: {
                light: themeColors.textSecondary.light,
                dark: themeColors.textSecondary.dark,
              },
            },
          },
          // "Price options" element — per-option price + detail lines. The price
          // follows the card price; detail lines follow the option description
          // (text-secondary). discountedFromPrice inherits from option.price via
          // the generic discounted-from rule, so it needs no static default here.
          price: {
            styles: {
              color: cards.price.styles.color,
            },
          },
          interval: {
            styles: {
              color: cards.price.styles.color,
            },
          },
          setupFee: {
            styles: {
              color: {
                light: themeColors.textSecondary.light,
                dark: themeColors.textSecondary.dark,
              },
            },
          },
          startDate: {
            styles: {
              color: {
                light: themeColors.textSecondary.light,
                dark: themeColors.textSecondary.dark,
              },
            },
          },
          planIterations: {
            styles: {
              color: {
                light: themeColors.textSecondary.light,
                dark: themeColors.textSecondary.dark,
              },
            },
          },
          trialPeriod: {
            styles: {
              color: {
                light: themeColors.textSecondary.light,
                dark: themeColors.textSecondary.dark,
              },
            },
          },
          prorationRenews: {
            styles: {
              color: {
                light: themeColors.textSecondary.light,
                dark: themeColors.textSecondary.dark,
              },
            },
          },
          prorationAmount: {
            styles: {
              color: {
                light: themeColors.textSecondary.light,
                dark: themeColors.textSecondary.dark,
              },
            },
          },
          indicator: {
            // Static fallback for the radio's inherited colours — the same values
            // the option card resolves to (which follow the global Cards), so the
            // editor swatch matches the render chain radio → option card → Cards.
            styles: {
              color: {
                light: themeColors.colorSecondary.light,
                dark: themeColors.colorSecondary.dark,
              },
              borderColor: cards.styles.borderColor,
              backgroundColor: cards.styles.backgroundColor,
            },
            selectedStyles: {
              backgroundColor: cards.selectedStyles.backgroundColor,
              borderColor: cards.selectedStyles.borderColor,
            },
          },
          // The pill is the Cards highlight until styled apart, so its swatches
          // are the Cards ones.
          badge: cards.badge,
        },
      },
      divider: {
        styles: {
          borderColor: {
            light: '#f0eeee', // gray-100
            dark: '#f0eeee', // same as light
          },
        },
      },
      description: {
        headings: {
          styles: {
            color: {
              light: themeColors.textPrimary.light,
              dark: themeColors.textPrimary.dark,
            },
          },
        },
        body: {
          styles: {
            color: {
              light: themeColors.textPrimary.light,
              dark: themeColors.textPrimary.dark,
            },
          },
        },
        links: {
          styles: {
            color: {
              light: themeColors.textPrimary.light,
              dark: themeColors.textPrimary.dark,
            },
          },
        },
        quotes: {
          styles: {
            color: {
              light: themeColors.textPrimary.light,
              dark: themeColors.textPrimary.dark,
            },
          },
        },
      },
      logo: {
        logoBanner: {
          styles: {
            color: {
              light: themeColors.textPrimary.light,
              dark: themeColors.textPrimary.dark,
            },
          },
        },
      },
      store: {
        storeName: {
          styles: {
            color: {
              light: themeColors.textPrimary.light,
              dark: themeColors.textPrimary.dark,
            },
          },
        },
        logoWithStoreName: {
          styles: {
            backgroundColor: {
              light: themeColors.colorPrimary.light,
              dark: themeColors.colorPrimary.dark,
            },
            color: {
              light: '#ffffff', // white
              dark: '#ffffff', // white
            },
          },
        },
      },
    },
    description: {
      headings: {
        styles: {
          color: {
            light: themeColors.textPrimary.light,
            dark: themeColors.textPrimary.dark,
          },
        },
      },
      body: {
        styles: {
          color: {
            light: themeColors.textPrimary.light,
            dark: themeColors.textPrimary.dark,
          },
        },
      },
      links: {
        styles: {
          color: {
            light: themeColors.textPrimary.light,
            dark: themeColors.textPrimary.dark,
          },
        },
      },
      quotes: {
        styles: {
          color: {
            light: themeColors.textPrimary.light,
            dark: themeColors.textPrimary.dark,
          },
        },
      },
      bullets: {
        styles: {
          color: {
            light: themeColors.textPrimary.light,
            dark: themeColors.textPrimary.dark,
          },
        },
      },
      collapsibles: {
        title: {
          styles: {
            color: {
              light: themeColors.textPrimary.light,
              dark: themeColors.textPrimary.dark,
            },
          },
        },
        body: {
          styles: {
            color: {
              light: themeColors.textPrimary.light,
              dark: themeColors.textPrimary.dark,
            },
          },
        },
        icon: {
          styles: {
            color: {
              light: themeColors.textPrimary.light,
              dark: themeColors.textPrimary.dark,
            },
          },
        },
        border: {
          styles: {
            borderColor: {
              light: '#e0d9dd', // gray-200
              dark: '#525252', // neutral-600
            },
          },
        },
      },
    },
  };
};

// Helper function to get default color for a specific path with inheritance resolution
export function getDefaultColorForPath(
  path,
  currentThemeValues = {},
  brandColor = null,
  layoutType = 'center',
) {
  const pathParts = path.split('.');

  // Remove 'variables' prefix if present
  if (pathParts[0] === 'variables') {
    pathParts.shift();
  }

  // A highlight's icon takes the highlight's text colour until it is set — the
  // saved text colour, else whatever the text colour itself defaults to — the
  // way a discounted-from price follows the price. Mirrors the render, where
  // the icon's CSS default is currentColor.
  const iconIndex = pathParts.indexOf('icon');
  if (
    iconIndex > 0 &&
    pathParts[iconIndex - 1] === 'badge' &&
    pathParts[pathParts.length - 1] === 'color'
  ) {
    const textPath = ['variables', ...pathParts.slice(0, iconIndex), 'styles', 'color'];
    const saved = getNestedValue(currentThemeValues, textPath);
    if (saved && (saved.light || saved.dark)) return saved;
    return getDefaultColorForPath(textPath.join('.'), currentThemeValues, brandColor);
  }

  // A divider takes the input border colour until it is set, as its CSS default does.
  if (pathParts.join('.') === 'elements.checkout.form.divider.styles.borderColor') {
    const inputBorder = [
      'variables',
      'elements',
      'checkout',
      'form',
      'fields',
      'input',
      'styles',
      'borderColor',
    ];
    const saved = getNestedValue(currentThemeValues, inputBorder);
    if (saved && (saved.light || saved.dark)) return saved;
    return getDefaultColorForPath(
      inputBorder.join('.'),
      currentThemeValues,
      brandColor,
      layoutType,
    );
  }

  // The bubble straddles the top of the content column, so it takes that
  // column's background for whichever layout the page renders with.
  if (pathParts.join('.') === 'elements.product.logo.logoBubble.styles.backgroundColor') {
    const layoutKey =
      { double: 'doubleLayout', split: 'splitLayout' }[layoutType] || 'centerLayout';
    const saved =
      getNestedValue(currentThemeValues, [
        'variables',
        'layout',
        layoutKey,
        'contentBackgroundColor',
      ]) || {};
    const fallback = getDefaultColorForPath(
      `variables.layout.${layoutKey}.contentBackgroundColor`,
      currentThemeValues,
      brandColor,
    );
    return { light: saved.light || fallback.light, dark: saved.dark || fallback.dark };
  }

  const colorPrimary = getNestedValue(currentThemeValues, ['variables', 'tokens', 'colorPrimary']);
  const colorSecondary = getNestedValue(currentThemeValues, [
    'variables',
    'tokens',
    'colorSecondary',
  ]);
  const textPrimary = getNestedValue(currentThemeValues, ['variables', 'tokens', 'textPrimary']);
  const textSecondary = getNestedValue(currentThemeValues, [
    'variables',
    'tokens',
    'textSecondary',
  ]);
  const textTertiary = getNestedValue(currentThemeValues, ['variables', 'tokens', 'textTertiary']);

  const priceBreakdownColor = getNestedValue(currentThemeValues, [
    'variables',
    'elements',
    'checkout',
    'form',
    'priceBreakdown',
    'styles',
    'color',
  ]);

  const defaultThemeColors = getDefaultThemeColors(
    colorPrimary,
    brandColor,
    textPrimary,
    textSecondary,
    colorSecondary,
    textTertiary,
    priceBreakdownColor,
  );

  // Handle token colors
  if (pathParts[0] === 'tokens') {
    const tokenType = pathParts[1];

    if (tokenType === 'colorPrimary' && colorPrimary) {
      return {
        light: colorPrimary.light || brandColor,
        dark: colorPrimary.dark || brandColor,
      };
    }

    if (tokenType === 'colorSecondary' && colorSecondary) {
      return {
        light: colorSecondary.light || colorPrimary?.light || brandColor,
        dark: colorSecondary.dark || colorPrimary?.dark || brandColor,
      };
    }

    if (defaultThemeColors[tokenType]) {
      return defaultThemeColors[tokenType];
    }
  }

  // Handle layout colors — per-layout color paths
  if (pathParts[0] === 'layout' && pathParts[1] === 'shadowColor') {
    return DEFAULT_LAYOUT_COLORS.shadowColor;
  }
  if (
    pathParts[0] === 'layout' &&
    ['centerLayout', 'doubleLayout', 'splitLayout'].includes(pathParts[1]) &&
    pathParts[2]
  ) {
    const layoutName = pathParts[1];
    const colorField = pathParts[2];

    // Layout colors inherit from global tokens
    // Mapping: bg→colorBackground, content→colorSurface (or colorSurfaceAccent for double), form→colorSurface
    const TOKEN_LAYOUT_MAP = {
      backgroundColor: 'colorBackground',
      contentBackgroundColor: layoutName === 'doubleLayout' ? 'colorSurfaceAccent' : 'colorSurface',
      formBackgroundColor: 'colorSurface',
    };

    const tokenKey = TOKEN_LAYOUT_MAP[colorField];
    if (tokenKey) {
      const tokenValue = getNestedValue(currentThemeValues, ['variables', 'tokens', tokenKey]);
      if (tokenValue && (tokenValue.light || tokenValue.dark)) {
        // Merge with the static default so empty channels still resolve
        const staticDefault = DEFAULT_LAYOUT_COLORS[layoutName]?.[colorField] || {};
        return {
          light: tokenValue.light || staticDefault.light,
          dark: tokenValue.dark || staticDefault.dark,
        };
      }
    }

    const layoutDefaults = DEFAULT_LAYOUT_COLORS[layoutName];
    if (layoutDefaults?.[colorField]) {
      return layoutDefaults[colorField];
    }
  }

  // Handle element colors with inheritance resolution
  if (pathParts[0] === 'elements') {
    const control = resolveOrderBumpControlDefault(pathParts, currentThemeValues, brandColor);
    if (control) return control;

    // First check for inheritance patterns (this takes priority over static defaults)
    const inheritedValue = resolveInheritedDefault(
      pathParts,
      currentThemeValues,
      defaultThemeColors,
    );

    if (inheritedValue) {
      return inheritedValue;
    }

    // If no inheritance, try to find the exact path in static defaults
    let current = getDefaultElementColors(defaultThemeColors);
    for (let i = 1; i < pathParts.length; i++) {
      if (current && current[pathParts[i]]) {
        current = current[pathParts[i]];
      } else {
        current = null;
        break;
      }
    }

    // If we found a color object with light/dark properties, return it
    if (current && typeof current === 'object' && ('light' in current || 'dark' in current)) {
      return current;
    }
  }

  return null;
}

// The order bump switch and accept button have no colours of their own until
// the seller paints them: the switch's off track and knob rest on the price
// picker's mark, its on track on the Main colour, and the accept button is the
// pay button in miniature, text included — resting on its resting colours,
// added on its hover ones; only a background of its own gets a text derived
// against it. The swatches follow the same chain, saved value first.
const ORDER_BUMPS_BASE = ['variables', 'elements', 'checkout', 'form', 'orderBumps'];
const PICKER_MARK_BASE = ['variables', 'elements', 'product', 'pricePicker', 'option', 'indicator'];
const PAY_BUTTON_BASE = ['variables', 'elements', 'checkout', 'form', 'button'];

function resolveOrderBumpControlDefault(pathParts, currentThemeValues, brandColor) {
  const at = pathParts.indexOf('orderBumps');
  const control = at === -1 ? null : pathParts[at + 1];
  if (control !== 'switch' && control !== 'acceptButton') return null;
  const subPath = pathParts.slice(at + 2).join('.');

  const read = parts => {
    const v = getNestedValue(currentThemeValues, parts);
    return v && (v.light || v.dark) ? v : null;
  };
  const resolve = parts =>
    read(parts) || getDefaultColorForPath(parts.join('.'), currentThemeValues, brandColor);
  const main = () => resolve(['variables', 'tokens', 'colorPrimary']);
  const readable = color =>
    color && {
      light: color.light ? getSafeReadableColor(color.light) : undefined,
      dark: color.dark ? getSafeReadableColor(color.dark) : undefined,
    };

  if (control === 'switch') {
    if (subPath === 'styles.backgroundColor')
      return resolve([...PICKER_MARK_BASE, 'styles', 'borderColor']);
    if (subPath === 'styles.color')
      return resolve([...PICKER_MARK_BASE, 'styles', 'backgroundColor']);
    if (subPath === 'selectedStyles.backgroundColor') return main();
    return null;
  }

  const button = [...ORDER_BUMPS_BASE, 'acceptButton'];
  const outline = getNestedValue(currentThemeValues, [...button, 'fill']) === 'outline';
  const pay = (state, field) => resolve([...PAY_BUTTON_BASE, state, field]);
  // An outline button sits on the bar behind it, which shows the card until
  // the seller paints it.
  const cardBackground = () =>
    read([...ORDER_BUMPS_BASE, 'acceptBar', 'styles', 'backgroundColor']) ||
    resolve([...ORDER_BUMPS_BASE, 'styles', 'backgroundColor']);
  const ownBackground = () => read([...button, 'styles', 'backgroundColor']);
  const ownBorder = () => read([...button, 'styles', 'borderColor']);
  const ownText = () => read([...button, 'styles', 'color']);
  const border = () => ownBorder() || pay('styles', 'borderColor');
  const selectedBorder = () =>
    read([...button, 'selectedStyles', 'borderColor']) ||
    ownBorder() ||
    pay('hoverStyles', 'borderColor');
  const text = () => {
    if (outline) return border();
    const own = ownBackground();
    return own ? readable(own) : pay('styles', 'color');
  };

  switch (subPath) {
    case 'styles.backgroundColor':
      return outline ? cardBackground() : pay('styles', 'backgroundColor');
    case 'selectedStyles.backgroundColor':
      return (
        ownBackground() || (outline ? cardBackground() : pay('hoverStyles', 'backgroundColor'))
      );
    case 'styles.borderColor':
      return pay('styles', 'borderColor');
    case 'selectedStyles.borderColor':
      return ownBorder() || pay('hoverStyles', 'borderColor');
    case 'styles.color':
      return text();
    case 'selectedStyles.color': {
      if (outline) return ownText() || selectedBorder();
      const selectedBackground = read([...button, 'selectedStyles', 'backgroundColor']);
      if (selectedBackground) return readable(selectedBackground);
      if (ownText()) return ownText();
      const own = ownBackground();
      return own ? readable(own) : pay('hoverStyles', 'color');
    }
    default:
      return null;
  }
}

// Map section sub-paths to the cards path they inherit from
const CARDS_INHERITANCE_MAP = {
  'styles.backgroundColor': 'styles.backgroundColor',
  'styles.borderColor': 'styles.borderColor',
  'selectedStyles.backgroundColor': 'selectedStyles.backgroundColor',
  'selectedStyles.borderColor': 'selectedStyles.borderColor',
  'name.styles.color': 'name.styles.color',
  'description.styles.color': 'description.styles.color',
  'price.styles.color': 'price.styles.color',
  'discountedFromPrice.styles.color': 'discountedFromPrice.styles.color',
  'planIterations.styles.color': 'detail.styles.color',
  'fee.styles.color': 'detail.styles.color',
  'remaining.styles.color': 'detail.styles.color',
  'saleDate.styles.color': 'detail.styles.color',
  'total.styles.color': 'detail.styles.color',
  'instructions.styles.color': 'detail.styles.color',
  'badge.styles.backgroundColor': 'badge.styles.backgroundColor',
};

// An order bump card names its parts differently from a Cards item (title, not
// name; a short description alongside the rich-text one), so it needs its own
// mapping rather than riding CARDS_INHERITANCE_MAP. Badge and indicator are
// absent on purpose: they follow the price option, not Cards.
const ORDER_BUMPS_INHERITANCE_MAP = {
  'styles.backgroundColor': 'styles.backgroundColor',
  'styles.borderColor': 'styles.borderColor',
  'selectedStyles.backgroundColor': 'selectedStyles.backgroundColor',
  'selectedStyles.borderColor': 'selectedStyles.borderColor',
  'title.styles.color': 'name.styles.color',
  'heading.styles.color': 'groupName.styles.color',
  'features.styles.color': 'name.styles.color',
  'callout.styles.color': 'name.styles.color',
  'callout.icon.styles.color': 'name.styles.color',
  'callout.styles.borderColor': 'styles.borderColor',
  'callToAction.styles.color': 'name.styles.color',
  'link.styles.color': 'name.styles.color',
  'shortDescription.styles.color': 'description.styles.color',
  'description.styles.color': 'description.styles.color',
  'price.styles.color': 'price.styles.color',
  'discountedFromPrice.styles.color': 'discountedFromPrice.styles.color',
  'setupFee.styles.color': 'detail.styles.color',
};

// Mirrors the cover fees selectors' CSS defaults, which never rest on the order
// bump's controls.
const FIELDS_INPUT_BASE = ['variables', 'elements', 'checkout', 'form', 'fields', 'input'];
const TOKENS_BASE = ['variables', 'tokens'];
const COVER_FEES_CONTROL_SOURCES = {
  'checkbox.styles.backgroundColor': [FIELDS_INPUT_BASE, 'styles.backgroundColor'],
  'checkbox.selectedStyles.backgroundColor': [TOKENS_BASE, 'colorSecondary'],
  'checkbox.styles.borderColor': [FIELDS_INPUT_BASE, 'styles.borderColor'],
  'checkbox.selectedStyles.borderColor': ['own', 'checkbox.styles.borderColor'],
  'checkbox.styles.color': ['white'],
  'switch.styles.backgroundColor': [PICKER_MARK_BASE, 'styles.borderColor'],
  'switch.selectedStyles.backgroundColor': [TOKENS_BASE, 'colorPrimary'],
  'switch.styles.color': [PICKER_MARK_BASE, 'styles.backgroundColor'],
  'indicator.styles.backgroundColor': [PICKER_MARK_BASE, 'styles.backgroundColor'],
  'indicator.selectedStyles.backgroundColor': [PICKER_MARK_BASE, 'selectedStyles.backgroundColor'],
  'indicator.styles.borderColor': [PICKER_MARK_BASE, 'styles.borderColor'],
  'indicator.selectedStyles.borderColor': [TOKENS_BASE, 'colorPrimary'],
  'indicator.styles.color': [TOKENS_BASE, 'colorPrimary'],
};

const CARDS_BADGE_BORDER = [
  'variables',
  'elements',
  'checkout',
  'cards',
  'badge',
  'styles',
  'borderColor',
];

// Group-level paths (variants.name, ticketGroups.name/description) map differently
const CARDS_GROUP_INHERITANCE_MAP = {
  'name.styles.color': 'groupName.styles.color',
  'description.styles.color': 'detail.styles.color',
};

function resolveCardsInheritance(pathParts, currentThemeValues) {
  // The heading's CSS default is the Cards group name colour, so a theme that
  // saves one (Dash's white) must show it here too, not the static token.
  if (pathParts.join('.') === 'elements.product.pricePicker.title.styles.color') {
    const value = getNestedValue(currentThemeValues, [
      'variables',
      'elements',
      'checkout',
      'cards',
      'groupName',
      'styles',
      'color',
    ]);
    return value && (value.light || value.dark) ? value : null;
  }

  // Multi-price picker options are card-like items too: their card surface
  // (background / border, incl. selected) plus Price and Discounted-from colours
  // inherit from the Cards element, matching how variant options behave (parity).
  // Scoped to the card-surface + price fields — the other option detail lines
  // keep their own (text-secondary) defaults.
  if (pathParts.includes('pricePicker') && pathParts.includes('option')) {
    const subPath = pathParts.slice(pathParts.indexOf('option') + 1).join('.');
    const OPTION_BASE = ['variables', 'elements', 'product', 'pricePicker', 'option'];
    const readSaved = parts => {
      const v = getNestedValue(currentThemeValues, parts);
      return v && (v.light || v.dark) ? v : null;
    };

    // Section-wide text Fill (option.styles.color): every text line except the
    // highlight consults it between its own value and its Cards tail —
    // mirroring the render's set-only mirror chain (see themePropertyRegistry).
    // Detail lines follow an EXPLICITLY-set option DESCRIPTION first (its
    // set-only mirror), then the Fill, then their own text-secondary default —
    // never the description's Cards tail.
    const OPTION_TEXT_SUBPATHS = [
      'label.styles.color',
      'description.styles.color',
      'price.styles.color',
      'discountedFromPrice.styles.color',
    ];
    const OPTION_DETAIL_SUBPATHS = [
      'setupFee.styles.color',
      'startDate.styles.color',
      'planIterations.styles.color',
      'trialPeriod.styles.color',
      'prorationRenews.styles.color',
      'prorationAmount.styles.color',
    ];
    if (OPTION_DETAIL_SUBPATHS.includes(subPath)) {
      const description = readSaved([...OPTION_BASE, 'description', 'styles', 'color']);
      if (description) return description;
      const fill = readSaved([...OPTION_BASE, 'styles', 'color']);
      if (fill) return fill;
    }
    if (OPTION_TEXT_SUBPATHS.includes(subPath)) {
      const fill = readSaved([...OPTION_BASE, 'styles', 'color']);
      if (fill) return fill;
    }

    // The Fill control's own swatch shows the Cards-derived look it defaults
    // to (card NAME colour) until the seller sets it.
    if (subPath === 'styles.color') {
      const cardName = readSaved([
        'variables',
        'elements',
        'checkout',
        'cards',
        'name',
        'styles',
        'color',
      ]);
      if (cardName) return cardName;
    }

    // The pill's border rests on its own background until set apart, so a
    // saved Cards pill border comes first and a saved Cards pill background
    // stands in for it, the way the render chains.
    if (subPath === 'badge.styles.borderColor') {
      return (
        readSaved(CARDS_BADGE_BORDER) ||
        readSaved([
          'variables',
          'elements',
          'checkout',
          'cards',
          'badge',
          'styles',
          'backgroundColor',
        ])
      );
    }

    const OPTION_CARD_SUBPATHS = [
      'styles.backgroundColor',
      'styles.borderColor',
      'selectedStyles.backgroundColor',
      'selectedStyles.borderColor',
      'description.styles.color',
      'price.styles.color',
      'discountedFromPrice.styles.color',
      'badge.styles.backgroundColor',
    ];
    if (OPTION_CARD_SUBPATHS.includes(subPath)) {
      const cardsSubPath = CARDS_INHERITANCE_MAP[subPath];
      const value = getNestedValue(currentThemeValues, [
        'variables',
        'elements',
        'checkout',
        'cards',
        ...cardsSubPath.split('.'),
      ]);
      if (value && (value.light || value.dark)) return value;
    }
  }

  if (pathParts.includes('orderBumps')) {
    const subPath = pathParts.slice(pathParts.indexOf('orderBumps') + 1).join('.');
    // A selected link is a shade darker than its resting colour until the
    // seller sets one apart, so the swatch darkens the resting colour, saved
    // or resolved. Unresolved, the base default carries the shaded Cards name.
    if (subPath === 'link.selectedStyles.color') {
      const saved = getNestedValue(currentThemeValues, [
        'variables',
        ...pathParts.slice(0, pathParts.indexOf('orderBumps') + 1),
        'link',
        'styles',
        'color',
      ]);
      const resting =
        saved && (saved.light || saved.dark)
          ? saved
          : resolveCardsInheritance(
              [
                ...pathParts.slice(0, pathParts.indexOf('orderBumps') + 1),
                'link',
                'styles',
                'color',
              ],
              currentThemeValues,
            );
      if (!resting) return null;
      return { light: shadeSelected(resting.light), dark: shadeSelected(resting.dark) };
    }
    // The link is the call to action's copy until dressed apart, so a call to
    // action colour the seller set comes before the Cards name both rest on.
    // The features list rests on the title's colour, so a title colour the
    // seller set comes before the Cards name both rest on.
    // The call out's text rests there too, and its icon on that text; its
    // background is a wash of that text over the card's.
    const bumpBase = ['variables', ...pathParts.slice(0, pathParts.indexOf('orderBumps') + 1)];
    const readBump = parts => {
      const v = getNestedValue(currentThemeValues, [...bumpBase, ...parts]);
      return v && (v.light || v.dark) ? v : null;
    };
    if (subPath === 'callout.icon.styles.color') {
      const text = readBump(['callout', 'styles', 'color']);
      if (text) return text;
    }
    if (
      subPath === 'features.styles.color' ||
      subPath === 'callout.styles.color' ||
      subPath === 'callout.icon.styles.color'
    ) {
      const title = readBump(['title', 'styles', 'color']);
      if (title) return title;
    }
    if (subPath === 'callout.styles.borderColor') {
      const own = readBump(['styles', 'borderColor']);
      if (own) return own;
    }
    if (subPath === 'callout.styles.backgroundColor') {
      const resolve = parts =>
        readBump(parts) ||
        getDefaultColorForPath([...bumpBase, ...parts].join('.'), currentThemeValues);
      return washOver(
        resolve(['callout', 'styles', 'color']),
        resolve(['styles', 'backgroundColor']),
      );
    }
    if (subPath === 'link.styles.color') {
      const callToAction = getNestedValue(currentThemeValues, [
        'variables',
        ...pathParts.slice(0, pathParts.indexOf('orderBumps') + 1),
        'callToAction',
        'styles',
        'color',
      ]);
      if (callToAction && (callToAction.light || callToAction.dark)) return callToAction;
    }
    // The bump pill is the price option pill until styled apart, and that one
    // is the Cards pill, so a saved option value comes before a saved Cards one.
    if (subPath === 'badge.styles.backgroundColor' || subPath === 'badge.styles.borderColor') {
      const read = parts => {
        const v = getNestedValue(currentThemeValues, parts);
        return v && (v.light || v.dark) ? v : null;
      };
      const option = [
        'variables',
        'elements',
        'product',
        'pricePicker',
        'option',
        'badge',
        'styles',
      ];
      const cards = ['variables', 'elements', 'checkout', 'cards', 'badge', 'styles'];
      const background = () =>
        read([...option, 'backgroundColor']) || read([...cards, 'backgroundColor']);
      if (subPath === 'badge.styles.backgroundColor') return background();
      return read([...option, 'borderColor']) || read([...cards, 'borderColor']) || background();
    }
    const cardsSubPath = ORDER_BUMPS_INHERITANCE_MAP[subPath];
    if (!cardsSubPath) return null;
    const value = getNestedValue(currentThemeValues, [
      'variables',
      'elements',
      'checkout',
      'cards',
      ...cardsSubPath.split('.'),
    ]);
    if (value && (value.light || value.dark)) return value;
    return null;
  }

  // The box rests on Cards, its label on Fields > Label and its amount on the
  // label, the way the checkout's CSS defaults chain.
  if (pathParts.includes('coverProcessingFees')) {
    const at = pathParts.indexOf('coverProcessingFees');
    const subPath = pathParts.slice(at + 1).join('.');
    const read = parts => {
      const v = getNestedValue(currentThemeValues, parts);
      return v && (v.light || v.dark) ? v : null;
    };
    const fieldsLabel = () =>
      read(['variables', 'elements', 'checkout', 'form', 'fields', 'label', 'styles', 'color']);
    if (subPath === 'label.styles.color') return fieldsLabel();
    const control = COVER_FEES_CONTROL_SOURCES[subPath];
    if (control) {
      const [source, sourceSubPath] = control;
      if (source === 'white') return { light: '#ffffff', dark: '#ffffff' };
      if (source === 'own') {
        return (
          read(['variables', ...pathParts.slice(0, at + 1), ...sourceSubPath.split('.')]) ||
          getDefaultColorForPath(
            ['variables', ...pathParts.slice(0, at + 1), sourceSubPath].join('.'),
            currentThemeValues,
          )
        );
      }
      const sourcePath = [...source, ...sourceSubPath.split('.')];
      return read(sourcePath) || getDefaultColorForPath(sourcePath.join('.'), currentThemeValues);
    }
    if (subPath === 'amount.styles.color') {
      return (
        read(['variables', ...pathParts.slice(0, at + 1), 'label', 'styles', 'color']) ||
        fieldsLabel()
      );
    }
    const cardsSubPath = CARDS_INHERITANCE_MAP[subPath];
    if (!cardsSubPath) return null;
    return read(['variables', 'elements', 'checkout', 'cards', ...cardsSubPath.split('.')]);
  }

  // Section-level card items (variantOptions, ticketTypes, manualPayments)
  for (const key of ['variantOptions', 'ticketTypes', 'manualPayments']) {
    if (!pathParts.includes(key)) continue;
    const subPath = pathParts.slice(pathParts.indexOf(key) + 1).join('.');
    const cardsSubPath = CARDS_INHERITANCE_MAP[subPath];
    if (!cardsSubPath) return null;
    const value = getNestedValue(currentThemeValues, [
      'variables',
      'elements',
      'checkout',
      'cards',
      ...cardsSubPath.split('.'),
    ]);
    if (value && (value.light || value.dark)) return value;
    return null;
  }

  // Group-level (variants.name, ticketGroups.name/description — NOT under variantOptions/ticketTypes)
  for (const key of ['variants', 'ticketGroups']) {
    if (!pathParts.includes(key)) continue;
    if (pathParts.includes('variantOptions') || pathParts.includes('ticketTypes')) continue;
    const subPath = pathParts.slice(pathParts.indexOf(key) + 1).join('.');
    const cardsSubPath = CARDS_GROUP_INHERITANCE_MAP[subPath];
    if (!cardsSubPath) return null;
    const value = getNestedValue(currentThemeValues, [
      'variables',
      'elements',
      'checkout',
      'cards',
      ...cardsSubPath.split('.'),
    ]);
    if (value && (value.light || value.dark)) return value;
    return null;
  }

  return null;
}

// Resolve inherited defaults for elements that inherit from parent elements
function resolveInheritedDefault(pathParts, currentThemeValues = {}, defaultThemeColors) {
  // Fields > Quantity selector mirrors its CSS defaults: the count box follows
  // Fields > Input (saved, then static), the icons the secondary text color.
  if (pathParts.includes('quantitySelector') && pathParts.includes('fields')) {
    const lastPart = pathParts[pathParts.length - 1];
    if (pathParts.includes('icon')) {
      return lastPart === 'color' ? defaultThemeColors?.textSecondary || null : null;
    }
    if (!['backgroundColor', 'color', 'borderColor'].includes(lastPart)) return null;
    const inputPath = ['variables', 'elements', 'checkout', 'form', 'fields', 'input', 'styles'];
    const saved = getNestedValue(currentThemeValues, [...inputPath, lastPart]);
    if (saved && (saved.light || saved.dark)) return saved;
    return getDefaultElementColors(defaultThemeColors)?.checkout?.form?.fields?.input?.styles?.[
      lastPart
    ];
  }

  // PWYW Interval / Currency suffix swatch: mirror the CSS chain
  // (subsection override → base override → that context's PWYW input placeholder,
  // saved → static). Suffix paths carry the combined key 'pwywSuffix' as a single
  // segment (subsections) or 'pwyw'+'suffix' (base), so they never collide with
  // the pwywInput/pwywLabel branch below.
  const isBasePwywSuffix = pathParts.includes('pwyw') && pathParts.includes('suffix');
  const isSubPwywSuffix = pathParts.includes('pwywSuffix');
  if ((isBasePwywSuffix || isSubPwywSuffix) && pathParts[pathParts.length - 1] === 'color') {
    const read = parts => {
      const v = getNestedValue(currentThemeValues, parts);
      return v && (v.light || v.dark) ? v : null;
    };
    // A subsection follows an explicitly-set BASE suffix first.
    if (isSubPwywSuffix) {
      const baseSuffix = read([
        'variables',
        'elements',
        'checkout',
        'pwyw',
        'suffix',
        'styles',
        'color',
      ]);
      if (baseSuffix) return baseSuffix;
    }
    // Then this context's PWYW input placeholder (saved), then the base input's.
    const inputBase = isSubPwywSuffix
      ? pathParts.includes('pricePicker')
        ? ['variables', 'elements', 'product', 'pricePicker', 'option', 'pwywInput']
        : pathParts.includes('orderBumps')
          ? ['variables', 'elements', 'checkout', 'form', 'orderBumps', 'pwywInput']
          : ['variables', 'elements', 'checkout', 'variants', 'variantOptions', 'pwywInput']
      : ['variables', 'elements', 'checkout', 'pwyw', 'input'];
    return (
      read([...inputBase, 'placeholderStyles', 'color']) ||
      read(['variables', 'elements', 'checkout', 'pwyw', 'input', 'placeholderStyles', 'color']) ||
        // Static Fields-input placeholder default (gray-300 / neutral-500).
        { light: '#c0b6ba', dark: '#737373' }
    );
  }

  if (pathParts[pathParts.length - 1] === 'color' && pathParts.includes('interval')) {
    const readColor = parts => {
      const v = getNestedValue(currentThemeValues, parts);
      return v && (v.light || v.dark) ? v : null;
    };
    if (pathParts.includes('pricePicker') && pathParts.includes('option')) {
      // Picker-option interval → the option Price, else the option Fill.
      const optionBase = ['variables', 'elements', 'product', 'pricePicker', 'option'];
      return (
        readColor([...optionBase, 'price', 'styles', 'color']) ||
        readColor([...optionBase, 'styles', 'color']) ||
        null
      );
    }
    if (pathParts.includes('pricing')) {
      // Single-price interval → the single-price Price.
      return (
        readColor(['variables', 'elements', 'product', 'pricing', 'price', 'styles', 'color']) ||
        null
      );
    }
  }

  // Radio indicator colours inherit so the design-editor default swatch matches
  // the checkout render. The two BACKGROUND fills now inherit Cards directly
  // (render: radio var → Cards var), so they skip the option-card hop. The two
  // BORDER colours still inherit through the option card (render: radio var →
  // option-card var → Cards var). Border radius + width stay custom.
  if (pathParts.includes('pricePicker') && pathParts.includes('indicator')) {
    const sub = pathParts.slice(pathParts.indexOf('indicator') + 1).join('.');
    const RADIO_BACKGROUND = ['styles.backgroundColor', 'selectedStyles.backgroundColor'];
    const RADIO_BORDER = ['styles.borderColor', 'selectedStyles.borderColor'];
    const readCards = () => {
      const cardSub = CARDS_INHERITANCE_MAP[sub];
      if (!cardSub) return null;
      const cardVal = getNestedValue(currentThemeValues, [
        'variables',
        'elements',
        'checkout',
        'cards',
        ...cardSub.split('.'),
      ]);
      return cardVal && (cardVal.light || cardVal.dark) ? cardVal : null;
    };
    if (RADIO_BACKGROUND.includes(sub)) {
      // Inherits the global Cards background directly — no option-card hop.
      const cardVal = readCards();
      if (cardVal) return cardVal;
    } else if (RADIO_BORDER.includes(sub)) {
      // 1) the user's Price-options "option card" value (more specific parent)
      const optionVal = getNestedValue(currentThemeValues, [
        'variables',
        'elements',
        'product',
        'pricePicker',
        'option',
        ...sub.split('.'),
      ]);
      if (optionVal && (optionVal.light || optionVal.dark)) return optionVal;
      // 2) the user's global Cards value (the option card itself inherits this)
      const cardVal = readCards();
      if (cardVal) return cardVal;
    }
  }

  // PWYW resolves through a multi-tier chain that mirrors the render:
  //   subsection (product.pricePicker.option.pwywInput/pwywLabel,
  //   checkout.variants.variantOptions.pwywInput) → base (checkout.pwyw.input/
  //   label) → tail.
  // The tail is Fields (checkout.form.fields.input/label) for every tier
  // EXCEPT the price-picker option's pwywLabel subsection, whose render
  // fallback additionally tries Price options > Label, then Fields > Label,
  // before bottoming out at the Cards-derived option label chain (see
  // pwywLabelGroup's `setTiers`/`fallback` in themePropertyRegistry.ts —
  // reconciling "base/subsection PWYW Label restyles it" with "an unstyled
  // PWYW option label looks like a fixed-price option label"). The base
  // pwyw.label element itself is unaffected and still bottoms out at Fields.
  // RESTING (styles.*): subsection → base saved → tail saved → tail static
  // default.
  // PLACEHOLDER (placeholderStyles.color, inputs only): subsection → base
  // saved → Fields PLACEHOLDER saved → Fields placeholder static default.
  // FOCUS (focusStyles.*): subsection focus → base focus (only when explicitly
  // set) → subsection resting saved → base resting saved → Fields FOCUS saved
  // (input or label slot) → tail saved → tail static default. This matches the
  // render var chains, where base focus vars are undefined-by-default so an
  // unset base focus falls back through the explicit resting values
  // (anti-flash) into the Fields FOCUS var — which itself defaults to the
  // Fields resting chain (see focusDefault in themePropertyRegistry.ts and the
  // single-price pwyw rules in checkout.css).
  //
  // Subsection paths carry the combined key 'pwywInput'/'pwywLabel' as a single
  // path segment, so they never also contain a bare 'pwyw' segment — this keeps
  // the subsection/base detection mutually exclusive.
  const isSubsectionPwywInput = pathParts.includes('pwywInput');
  const isSubsectionPwywLabel = pathParts.includes('pwywLabel');
  const isBasePwywInput =
    !isSubsectionPwywInput && pathParts.includes('pwyw') && pathParts.includes('input');
  const isBasePwywLabel =
    !isSubsectionPwywLabel && pathParts.includes('pwyw') && pathParts.includes('label');

  if (isSubsectionPwywInput || isSubsectionPwywLabel || isBasePwywInput || isBasePwywLabel) {
    const isSubsection = isSubsectionPwywInput || isSubsectionPwywLabel;
    const isInputKind = isSubsectionPwywInput || isBasePwywInput;
    const fieldsKey = isInputKind ? 'input' : 'label';
    const lastPart = pathParts[pathParts.length - 1];
    const validLeaves = isInputKind ? ['backgroundColor', 'color', 'borderColor'] : ['color'];

    if (validLeaves.includes(lastPart)) {
      const isFocus = pathParts.includes('focusStyles');

      const readSaved = parts => {
        const value = getNestedValue(currentThemeValues, parts);
        return value && (value.light || value.dark) ? value : null;
      };

      // The base checkout.pwyw.<input|label> value at a given tier ('styles' or
      // 'focusStyles') — this IS the element itself when isBasePwyw*, or the
      // parent tier when isSubsection*.
      const readBasePwyw = tier => [
        'variables',
        'elements',
        'checkout',
        'pwyw',
        fieldsKey,
        tier,
        lastPart,
      ];

      // Fields (checkout.form.fields.<input|label>) saved value → Fields static
      // default. This is the chain tail for every tier except the price-picker
      // option's pwywLabel subsection (see resolveOptionLabelTail below).
      // `tier` picks which Fields slot the tail reads — placeholder leaves
      // read the Fields placeholder, everything else the resting styles.
      const resolveFieldsTail = (tier = 'styles') => {
        const fieldsSaved = readSaved([
          'variables',
          'elements',
          'checkout',
          'form',
          'fields',
          fieldsKey,
          tier,
          lastPart,
        ]);
        if (fieldsSaved) return fieldsSaved;
        const elementColors = getDefaultElementColors(defaultThemeColors);
        return elementColors?.checkout?.form?.fields?.[fieldsKey]?.[tier]?.[lastPart] || null;
      };

      // The price-picker option's pwywLabel subsection chain tail: Price
      // options > Label saved (product.pricePicker.option.label.styles.color)
      // → Fields > Label saved (checkout.form.fields.label.styles.color) →
      // Cards > Name saved (checkout.cards.name.styles.color) → Cards static
      // default. Mirrors the render's --cp_price-picker-option-pwywLabel-color
      // structural fallback (var(--cp_pwyw-label-color,
      // var(--cp_price-picker-option-label-color-set,
      // var(--cp_checkout-form-fields-label-color-set,
      // var(--cp_price-picker-option-label-color))))), where
      // --cp_price-picker-option-label-color itself defaults to
      // --cp_card-name-color. The `-set` mirror vars only matter at render
      // time (to tell "explicitly set" apart from "showing its own default");
      // here we're resolving against SAVED theme values, so a plain saved-value
      // check at each tier is equivalent.
      const resolveOptionLabelTail = () => {
        const optionLabelSaved = readSaved([
          'variables',
          'elements',
          'product',
          'pricePicker',
          'option',
          'label',
          'styles',
          'color',
        ]);
        if (optionLabelSaved) return optionLabelSaved;
        const fieldsLabelSaved = readSaved([
          'variables',
          'elements',
          'checkout',
          'form',
          'fields',
          'label',
          'styles',
          'color',
        ]);
        if (fieldsLabelSaved) return fieldsLabelSaved;
        const cardNameSaved = readSaved([
          'variables',
          'elements',
          'checkout',
          'cards',
          'name',
          'styles',
          'color',
        ]);
        if (cardNameSaved) return cardNameSaved;
        const elementColors = getDefaultElementColors(defaultThemeColors);
        return elementColors?.checkout?.cards?.name?.styles?.color || null;
      };

      // Only the price-picker option's LABEL subsection swaps its tail —
      // every other tier (base label/input, subsection input, variant input)
      // keeps the Fields tail unchanged.
      const resolveTail = isSubsectionPwywLabel ? resolveOptionLabelTail : resolveFieldsTail;

      // PLACEHOLDER leaf (inputs only — labels have no placeholder):
      // subsection → base saved → Fields placeholder saved → Fields
      // placeholder static default, mirroring the render chain
      // (--cp_pwyw-input-placeholder-color defaults to the Fields
      // placeholder var, never the text color).
      if (pathParts.includes('placeholderStyles')) {
        if (isSubsection) {
          return (
            readSaved(readBasePwyw('placeholderStyles')) || resolveFieldsTail('placeholderStyles')
          );
        }
        return resolveFieldsTail('placeholderStyles');
      }

      if (!isFocus) {
        // RESTING leaf.
        if (isSubsection) {
          return readSaved(readBasePwyw('styles')) || resolveTail();
        }
        return resolveTail();
      }

      // FOCUS leaf. Both kinds consult the Fields FOCUS saved value (input or
      // label slot via fieldsKey) after the explicit resting tiers, mirroring
      // the render chains' Fields focus var tail (see the pwyw focus rules in
      // checkout.css and the generated subsection chains).
      const readFieldsFocusSaved = () =>
        readSaved([
          'variables',
          'elements',
          'checkout',
          'form',
          'fields',
          fieldsKey,
          'focusStyles',
          lastPart,
        ]);
      if (isSubsection) {
        // Subsection's own resting saved value lives at the sibling 'styles'
        // path of this focusStyles path — look it up first, after base focus.
        const focusIndex = pathParts.indexOf('focusStyles');
        const ownRestingPath = ['variables', ...pathParts.slice(0, focusIndex), 'styles', lastPart];
        return (
          readSaved(readBasePwyw('focusStyles')) ||
          readSaved(ownRestingPath) ||
          readSaved(readBasePwyw('styles')) ||
          readFieldsFocusSaved() ||
          resolveTail()
        );
      }
      return readSaved(readBasePwyw('styles')) || readFieldsFocusSaved() || resolveTail();
    }
  }

  // Cards inheritance — section fields inherit from cards when a cards value is set
  const cardsValue = resolveCardsInheritance(pathParts, currentThemeValues);
  if (cardsValue) return cardsValue;

  // Focus styles inherit from regular styles (e.g., focusStyles.borderColor inherits from styles.borderColor)
  if (pathParts.includes('focusStyles')) {
    const lastPart = pathParts[pathParts.length - 1];
    // Handle color properties that should inherit from styles
    if (lastPart === 'borderColor' || lastPart === 'backgroundColor' || lastPart === 'color') {
      // Build the path to the corresponding styles property
      const focusStylesIndex = pathParts.indexOf('focusStyles');
      const stylesPath = ['variables', ...pathParts.slice(0, focusStylesIndex), 'styles', lastPart];
      const stylesValue = getNestedValue(currentThemeValues, stylesPath);

      if (stylesValue && (stylesValue.light || stylesValue.dark)) {
        return stylesValue;
      }

      // Also check in the static defaults
      const elementColors = getDefaultElementColors(defaultThemeColors);
      let current = elementColors;
      for (let i = 1; i < focusStylesIndex; i++) {
        if (current && current[pathParts[i]]) {
          current = current[pathParts[i]];
        } else {
          current = null;
          break;
        }
      }
      if (current?.styles?.[lastPart]) {
        return current.styles[lastPart];
      }
    }
  }

  // Discounted from price inherits from price (for variants, ticket types, and product pricing)
  if (pathParts.includes('discountedFromPrice')) {
    const discountedIndex = pathParts.indexOf('discountedFromPrice');
    const lastPart = pathParts[pathParts.length - 1];
    // Build path to the corresponding price element's property
    const pricePath = [
      'variables',
      ...pathParts.slice(0, discountedIndex),
      'price',
      ...pathParts.slice(discountedIndex + 1),
    ];
    const priceValue = getNestedValue(currentThemeValues, pricePath);

    if (priceValue && (typeof priceValue !== 'object' || priceValue.light || priceValue.dark)) {
      return priceValue;
    }

    // Also check in the static defaults
    const elementColors = getDefaultElementColors(defaultThemeColors);
    let current = elementColors;
    for (let i = 1; i < discountedIndex; i++) {
      if (current && current[pathParts[i]]) {
        current = current[pathParts[i]];
      } else {
        current = null;
        break;
      }
    }
    if (current?.price?.styles?.[lastPart]) {
      return current.price.styles[lastPart];
    }
  }

  // Price breakdown inheritance patterns
  if (pathParts.includes('priceBreakdown')) {
    // Check if this is a specific price breakdown item (not the main priceBreakdown)
    const priceBreakdownIndex = pathParts.indexOf('priceBreakdown');
    const isSpecificItem =
      priceBreakdownIndex < pathParts.length - 1 && pathParts[priceBreakdownIndex + 1] !== 'styles'; // Not the main priceBreakdown styles

    if (isSpecificItem) {
      // Check if there's a parent "all items" value set
      const parentPath = [
        'variables',
        'elements',
        'checkout',
        'form',
        'priceBreakdown',
        'styles',
        'color',
      ];
      const parentValue = getNestedValue(currentThemeValues, parentPath);

      if (parentValue && (parentValue.light || parentValue.dark)) {
        return parentValue;
      }

      // Also check for font settings inheritance
      const lastPart = pathParts[pathParts.length - 1];
      if (lastPart === 'fontFamily' || lastPart === 'fontSize' || lastPart === 'fontWeight') {
        const fontParentPath = [
          'variables',
          'elements',
          'checkout',
          'form',
          'priceBreakdown',
          'styles',
          lastPart,
        ];
        const fontParentValue = getNestedValue(currentThemeValues, fontParentPath);
        if (fontParentValue) {
          return fontParentValue;
        }
      }
    }

    const elementColors = getDefaultElementColors(defaultThemeColors);

    // Fall back to static default
    return elementColors.checkout?.form?.priceBreakdown?.styles?.color || null;
  }

  // Variant options inheritance
  if (pathParts.includes('variantOptions')) {
    const variantOptionsIndex = pathParts.indexOf('variantOptions');
    const isSpecificItem =
      variantOptionsIndex < pathParts.length - 1 && pathParts[variantOptionsIndex + 1] !== 'styles'; // Not the main variantOptions styles

    if (isSpecificItem) {
      // Check if there's a parent variant color value set
      const parentPath = [
        'variables',
        'elements',
        'checkout',
        'variants',
        'variantOptions',
        'styles',
        'color',
      ];
      const parentValue = getNestedValue(currentThemeValues, parentPath);

      if (parentValue && (parentValue.light || parentValue.dark)) {
        return parentValue;
      }

      // Also check for font settings inheritance
      const lastPart = pathParts[pathParts.length - 1];
      if (lastPart === 'fontFamily' || lastPart === 'fontSize' || lastPart === 'fontWeight') {
        const fontParentPath = [
          'variables',
          'elements',
          'checkout',
          'variants',
          'variantOptions',
          'styles',
          lastPart,
        ];
        const fontParentValue = getNestedValue(currentThemeValues, fontParentPath);
        if (fontParentValue) {
          return fontParentValue;
        }
      }
    }
  }

  // Button text color inherits contrast from button background color
  // pathParts: ['elements', 'checkout', 'form', 'button', 'styles'|'hoverStyles', 'color']
  {
    const buttonIndex = pathParts.indexOf('button');
    const lastPart = pathParts[pathParts.length - 1];
    if (buttonIndex !== -1 && pathParts[buttonIndex - 1] === 'form' && lastPart === 'color') {
      // Check if there's an explicit button background color set
      const isHover = pathParts.includes('hoverStyles');
      const bgPath = isHover
        ? ['variables', 'elements', 'checkout', 'form', 'button', 'hoverStyles', 'backgroundColor']
        : ['variables', 'elements', 'checkout', 'form', 'button', 'styles', 'backgroundColor'];
      const bgValue = getNestedValue(currentThemeValues, bgPath);

      // Fall back to contrast against colorPrimary for any mode without an explicit bg
      const colorPrimary =
        getNestedValue(currentThemeValues, ['variables', 'tokens', 'colorPrimary']) ||
        defaultThemeColors.colorPrimary;
      const primaryLight = colorPrimary?.light || defaultThemeColors.colorPrimary.light;
      const primaryDark = colorPrimary?.dark || defaultThemeColors.colorPrimary.dark;

      const deriveLightFallback = () =>
        isHover
          ? getSafeReadableColor(darken(primaryLight, 0.1))
          : getSafeReadableColor(primaryLight);
      const deriveDarkFallback = () =>
        isHover
          ? getSafeReadableColor(darken(primaryDark, 0.1))
          : getSafeReadableColor(primaryDark);

      return {
        light: bgValue?.light ? getSafeReadableColor(bgValue.light) : deriveLightFallback(),
        dark: bgValue?.dark ? getSafeReadableColor(bgValue.dark) : deriveDarkFallback(),
      };
    }
  }

  // Highlight badge text colour inherits contrast from the badge's effective
  // background (explicit badge bg, else the secondary accent), mirroring the
  // CheckoutCSS runtime derivation, like the button text above. The single-price
  // badge (product.pricing.badge) and the Cards badge (checkout.cards.badge)
  // each derive against their own background. The multi-price option badge
  // chains to the Cards one: a background of its own derives a text of its own,
  // otherwise it shows the Cards text, saved or derived.
  {
    const badgeIndex = pathParts.indexOf('badge');
    const lastPart = pathParts[pathParts.length - 1];
    const owner = badgeIndex === -1 ? null : pathParts[badgeIndex - 1];
    if (
      badgeIndex !== -1 &&
      lastPart === 'color' &&
      pathParts[badgeIndex + 1] === 'styles' &&
      (owner === 'pricing' || owner === 'option' || owner === 'cards' || owner === 'orderBumps')
    ) {
      const read = parts => {
        const v = getNestedValue(currentThemeValues, parts);
        return v && (v.light || v.dark) ? v : null;
      };
      const bgPath = [
        'variables',
        ...pathParts.slice(0, badgeIndex + 1),
        'styles',
        'backgroundColor',
      ];
      const bgValue = getNestedValue(currentThemeValues, bgPath);

      const colorSecondary =
        getNestedValue(currentThemeValues, ['variables', 'tokens', 'colorSecondary']) ||
        defaultThemeColors.colorSecondary;
      const secondaryLight = colorSecondary?.light || defaultThemeColors.colorSecondary.light;
      const secondaryDark = colorSecondary?.dark || defaultThemeColors.colorSecondary.dark;

      if (owner === 'orderBumps') {
        const optionText = 'variables.elements.product.pricePicker.option.badge.styles.color';
        const option =
          read(optionText.split('.')) ||
          getDefaultColorForPath(optionText, currentThemeValues) ||
          {};
        const derive = mode =>
          bgValue?.[mode] ? getSafeReadableColor(bgValue[mode]) : option[mode];
        return { light: derive('light'), dark: derive('dark') };
      }

      if (owner === 'option') {
        const cardsBase = ['variables', 'elements', 'checkout', 'cards', 'badge', 'styles'];
        const cardsText = read([...cardsBase, 'color']) || {};
        const cardsBg = getNestedValue(currentThemeValues, [...cardsBase, 'backgroundColor']);
        const derive = mode =>
          bgValue?.[mode]
            ? getSafeReadableColor(bgValue[mode])
            : cardsText[mode] ||
              getSafeReadableColor(
                cardsBg?.[mode] || (mode === 'light' ? secondaryLight : secondaryDark),
              );
        return { light: derive('light'), dark: derive('dark') };
      }

      return {
        light: getSafeReadableColor(bgValue?.light || secondaryLight),
        dark: getSafeReadableColor(bgValue?.dark || secondaryDark),
      };
    }
  }

  // Ticket types inheritance
  if (pathParts.includes('ticketTypes')) {
    const ticketTypesIndex = pathParts.indexOf('ticketTypes');
    const isSpecificItem =
      ticketTypesIndex < pathParts.length - 1 && pathParts[ticketTypesIndex + 1] !== 'styles'; // Not the main ticketTypes styles

    if (isSpecificItem) {
      // Check if there's a parent ticket types color value set
      const parentPath = [
        'variables',
        'elements',
        'checkout',
        'ticketGroups',
        'ticketTypes',
        'styles',
        'color',
      ];
      const parentValue = getNestedValue(currentThemeValues, parentPath);

      if (parentValue && (parentValue.light || parentValue.dark)) {
        return parentValue;
      }

      // Also check for font settings inheritance
      const lastPart = pathParts[pathParts.length - 1];
      if (lastPart === 'fontFamily' || lastPart === 'fontSize' || lastPart === 'fontWeight') {
        const fontParentPath = [
          'variables',
          'elements',
          'checkout',
          'ticketGroups',
          'ticketTypes',
          'styles',
          lastPart,
        ];
        const fontParentValue = getNestedValue(currentThemeValues, fontParentPath);
        if (fontParentValue) {
          return fontParentValue;
        }
      }
    }
  }

  return null;
}

/**
 * Strips values from `variables` that match system defaults.
 * Includes both BASE_VARIABLES_DEFAULTS (structural defaults) and
 * dynamic element color defaults from getDefaultElementColors().
 * Themes should only store intentional customizations, not redundant defaults.
 */
/**
 * Returns BASE_VARIABLES_DEFAULTS merged with dynamic element/layout color defaults.
 * Used as the complete reference for what constitutes a "system default".
 */
// Built once: the tree depends on nothing but module constants, and the field
// builder asks for it once per field on every rebuild of the design form. Callers
// read from it and must not mutate it.
let fullVariableDefaults = null;

export function getFullVariableDefaults() {
  if (fullVariableDefaults) return fullVariableDefaults;
  const defaultThemeColors = getDefaultThemeColors(null, null, null, null, null, null);
  const defaultElementColors = getDefaultElementColors(defaultThemeColors);
  fullVariableDefaults = {
    ...BASE_VARIABLES_DEFAULTS,
    elements: deepMergeForDefaults(BASE_VARIABLES_DEFAULTS.elements, defaultElementColors),
    layout: deepMergeForDefaults(BASE_VARIABLES_DEFAULTS.layout, DEFAULT_LAYOUT_COLORS),
  };
  return fullVariableDefaults;
}

export function stripSystemDefaults(variables) {
  return stripDefaults(variables, getFullVariableDefaults(), [], variables);
}

function colorsEqual(a, b) {
  if (a === b) return true;
  if (typeof a === 'string' && typeof b === 'string') {
    return a.toLowerCase() === b.toLowerCase();
  }
  return false;
}

function deepMergeForDefaults(target, source) {
  if (!target || typeof target !== 'object') return source;
  if (!source || typeof source !== 'object') return target;
  const result = { ...target };
  for (const key of Object.keys(source)) {
    const sv = source[key];
    const tv = target[key];
    if (
      sv &&
      typeof sv === 'object' &&
      !Array.isArray(sv) &&
      !(sv.light !== undefined || sv.dark !== undefined)
    ) {
      result[key] = deepMergeForDefaults(tv, sv);
    } else {
      result[key] = sv;
    }
  }
  return result;
}

function stripDefaults(obj, defaults, path = [], rootVariables = obj) {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return obj;
  if (!defaults || typeof defaults !== 'object') return obj;

  const result = {};
  for (const key of Object.keys(obj)) {
    const val = obj[key];
    const def = defaults[key];
    const nextPath = [...path, key];

    if (val === null || val === undefined) continue;
    if (val === '' && def === undefined) continue; // empty string = cleared value
    if (val === def) continue; // same primitive
    // Numeric coercion: select fields save strings but defaults are numbers
    if (
      typeof val !== typeof def &&
      val !== '' &&
      def !== '' &&
      Number(val) === Number(def) &&
      !isNaN(Number(val))
    )
      continue;

    if (typeof val === 'object' && !Array.isArray(val)) {
      // Color objects {light, dark} — strip individual channels matching defaults
      if (val.light !== undefined || val.dark !== undefined) {
        let defColor = typeof def === 'object' && def !== null ? def : { light: '', dark: '' };
        const fullPath = ['variables', ...nextPath].join('.');
        if (
          fullPath === 'variables.elements.checkout.form.button.styles.color' ||
          fullPath === 'variables.elements.checkout.form.button.hoverStyles.color'
        ) {
          defColor = getDefaultColorForPath(fullPath, { variables: rootVariables }) || defColor;
        }
        const stripped = {};
        if (val.light !== undefined && !colorsEqual(val.light, defColor.light)) {
          stripped.light = val.light;
        }
        if (val.dark !== undefined && !colorsEqual(val.dark, defColor.dark)) {
          stripped.dark = val.dark;
        }
        if (Object.keys(stripped).length > 0) {
          result[key] = stripped;
        }
        continue;
      }
      // Recurse into nested objects
      const stripped = stripDefaults(
        val,
        typeof def === 'object' ? def : {},
        nextPath,
        rootVariables,
      );
      if (stripped && Object.keys(stripped).length > 0) {
        result[key] = stripped;
      }
      continue;
    }

    // enabled=true with default true or no default → skip
    if (val === true && key === 'enabled' && (def === true || def === undefined)) continue;

    result[key] = val;
  }

  return Object.keys(result).length > 0 ? result : undefined;
}

// Helper function to safely get nested object values
function getNestedValue(obj, path) {
  return path.reduce((current, key) => {
    return current && current[key] !== undefined ? current[key] : undefined;
  }, obj);
}

// What's next — three candidates I'm aware of (there's no tasks/todo.md to pull from):

// 1. PWYW label focus swatch — while fixing issue 5 I deliberately scoped the resolver fix to inputs. The label focus swatch likely has the same desync: a saved Fields > Label focus color renders on the checkout but probably won't show in the "Pay what you want > Label" focus swatch. Small, same pattern.
// 2. Focus ring color nuance — the 3px focus ring always uses the Fields focus border color. If a seller overrides the base PWYW focus border, the border changes but the ring stays Fields-colored. Defensible as-is, but it's the one remaining place border and ring can disagree.
// 3. Housekeeping — the redundant stash@{0} from the earlier stash incident is still sitting there; a git stash drop from you clears it.

// ▎ 1. One-time/pwyw pricing variants can select recurring as the billing type. This shouldn't be possible. Only subscriptions allow recurring.
// ▎ 2. Fields > input not applying padding Y to variant pwyw
// ▎ 3. Fields > input border focus colour is seemingly only applying an outline. Compare a field like customer_name to variants pwyw + the single pwyw input + multi price pwyw input. Border color does apply correctly. Use that as the benchmark.
// ▎ 4. Fields > input focus colour not being applied to variants pwyw + the single pwyw input + the multi price pwyw
// ▎ 5. Colours from fields > input aren't being updated/inherited into pay what you want > input or pay what you want > product variants pwyw > input or pay what you want > price options pwyw > input. The dashboard should inherit the colour visually. The colours are being inherited from pay what you want > input into its children.

// Okay good job. Next set of tasks

// 1. PWYW label focus swatch - yes please do this, it's not just the focus color either, its the label color as well.
// 2. fields > label focus colour not being applied to multi price pay what you want (it is being applied to single price pay what you want and variants have no label).
// 3. I think the focus ring color nuance needs to be fixed. focus ring should inherit from the focus border color of the highest priority.
